import { FC } from 'react'

import { Chip, ChipProps, Tooltip, TooltipContent, TooltipTrigger } from '@forto-dev/phoenix-ui-core'

import { BookingDTOState } from '@/clients/booking'
import { BookingStateChip } from '@/features/bookings'
import { useLocalisation } from '@/utils/localisation'

import { OverviewItem } from '../domain/overviewItem'
import { Shipment } from '../domain/shipment'
import { ShipmentDelayBasis } from '../utils/getTimelineData'

type StateChipProps = { variant: ChipProps['variant']; label: string }

// Graded colouring for delayed shipments: blue (minor) -> orange (moderate) -> red (severe).
const getDelayVariant = (delayDays: number): ChipProps['variant'] => {
  if (delayDays >= 21) {
    return 'negative'
  }
  if (delayDays >= 5) {
    return 'warning'
  }
  return 'primary'
}

// TODO: Move to translations once the texts are confirmed
const useShipmentStateChipProps = (): Record<Shipment['state'], StateChipProps> => {
  const { translate } = useLocalisation()

  return {
    active: {
      variant: 'primary',
      label: translate('overview.state.onTime'),
    },
    new: {
      variant: 'primary',
      label: translate('overview.state.onTime'),
    },
    completed: { variant: 'primary', label: translate('overview.state.completed') },
    closed: { variant: 'neutral', label: translate('overview.state.canceled') },
    canceled: {
      variant: 'neutral',
      label: translate('overview.state.canceled'),
    },
    shipmentCreated: {
      variant: 'positive',
      label: translate('integrations.subscriptions.types.shipmentCreated'),
    },
  }
}

const useGetStateChipProps = (
  state: Shipment['state'] | 'delayed',
  delayDays?: number,
  delayBasis?: ShipmentDelayBasis
): StateChipProps => {
  const { translate } = useLocalisation()
  const stateChipPropsMap = useShipmentStateChipProps()
  if (state === 'delayed') {
    const label =
      delayBasis === 'arrival'
        ? translate('overview.state.delayedArrivalBadge', { delayDays })
        : translate('overview.state.delayed', { delayDays })
    return { variant: getDelayVariant(delayDays ?? 0), label }
  }
  return stateChipPropsMap[state]
}

type ShipmentStateChipProps = {
  state: Shipment['state'] | 'delayed'
  delayDays?: number
  delayBasis?: ShipmentDelayBasis
}

export const ShipmentStateChip: FC<ShipmentStateChipProps> = ({ state, delayDays, delayBasis }) => {
  const stateChipProps = useGetStateChipProps(state, delayDays, delayBasis)
  return (
    <Tooltip>
      <TooltipTrigger>
        <Chip variant={stateChipProps.variant}>{stateChipProps.label}</Chip>
      </TooltipTrigger>
      <TooltipContent>{stateChipProps.label}</TooltipContent>
    </Tooltip>
  )
}

export const OverviewItemStatus: FC<{
  state: OverviewItem['state'] | 'delayed'
  type?: OverviewItem['type']
  delayDays?: number
  delayBasis?: ShipmentDelayBasis
}> = ({ type, state, delayDays, delayBasis }) => {
  if (type === 'booking') {
    return <BookingStateChip state={state as BookingDTOState} />
  }
  return <ShipmentStateChip state={state as Shipment['state']} delayDays={delayDays} delayBasis={delayBasis} />
}
